import React from 'react';
import { Box, Button, CircularProgress, makeStyles } from '@material-ui/core';
import ReorderIcon from '@material-ui/icons/Reorder';
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import { useMapApiValidation } from './hooks/useMapApiValidation';
import { useIsMobile } from '../../hooks/useIsMobile';
import { CRM_COLORS, CRM_RADIUS } from '../../theme/crmColors';
import { BREAKPOINTS } from '../../theme/breakpoints';

export type ViewMode = 'cards' | 'table' | 'map';

interface ViewToggleProps {
    viewMode: ViewMode;
    setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>;
}

const useStyles = makeStyles(() => ({
    container: {
        display: 'flex',
        gap: 2,
        padding: 3,
        borderRadius: CRM_RADIUS.control,
        backgroundColor: CRM_COLORS.segmented,
    },
    button: {
        height: 36,
        borderRadius: CRM_RADIUS.control,
        border: 0,
        fontWeight: 500,
        fontSize: '0.875rem',
        padding: '0 16px',
        minWidth: 0,
        boxShadow: 'none',
        whiteSpace: 'nowrap',
        color: CRM_COLORS.text.secondary,
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: 'transparent',
            color: CRM_COLORS.secondary,
            boxShadow: 'none',
        },
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            padding: '0 10px',
        },
    },
    buttonActive: {
        backgroundColor: CRM_COLORS.surface,
        color: CRM_COLORS.secondary,
        fontWeight: 600,
        boxShadow: '0 1px 2px rgba(9, 43, 75, 0.12)',
        '&:hover': {
            backgroundColor: CRM_COLORS.surface,
            color: CRM_COLORS.secondary,
            boxShadow: '0 1px 2px rgba(9, 43, 75, 0.12)',
        },
    },
    buttonContent: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    icon: {
        fontSize: 16,
    },
    spinner: {
        marginLeft: 2,
    },
}));

const ViewToggle: React.FC<ViewToggleProps> = ({ viewMode, setViewMode }) => {
    const classes = useStyles();
    const { isValid, isChecking } = useMapApiValidation();
    const isMobile = useIsMobile();

    const allOptions: { value: ViewMode; label: string; Icon: React.ElementType }[] = [
        { value: 'cards', label: 'Cartes', Icon: ViewModuleIcon },
        { value: 'table', label: 'Tableau', Icon: ReorderIcon },
        { value: 'map', label: 'Map', Icon: RoomOutlinedIcon },
    ];

    // En móvil la tabla no cabe bien: solo se ofrecen Tarjetas y Mapa.
    const options = isMobile ? allOptions.filter((o) => o.value !== 'table') : allOptions;

    // Si en escritorio estaba activa la tabla y se pasa a móvil, resaltamos Tarjetas.
    const activeViewMode: ViewMode = isMobile && viewMode === 'table' ? 'cards' : viewMode;

    return (
        <Box className={classes.container}>
            {options.map(({ value, label, Icon }) => {
                const isMapButton = value === 'map';
                const isTableButton = value === 'table';
                const needsValidation = isMapButton || isTableButton;
                const isDisabled = needsValidation && (!isValid || isChecking);
                const isActive = activeViewMode === value;

                return (
                    <Button
                        key={value}
                        aria-pressed={isActive}
                        onClick={() => setViewMode(value)}
                        className={`${classes.button} ${isActive ? classes.buttonActive : ''}`}
                        disabled={isDisabled}
                        size='small'
                    >
                        <span className={classes.buttonContent}>
                            <Icon className={classes.icon} />
                            {label}
                            {needsValidation && isChecking && (
                                <CircularProgress size={14} className={classes.spinner} color='inherit' />
                            )}
                        </span>
                    </Button>
                );
            })}
        </Box>
    );
};

export default ViewToggle;
